import { useState, useEffect, useCallback, useMemo } from 'react';
import { clockApi, userApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  Users, LogIn, LogOut, Clock, Search, RefreshCw, Loader,
  CalendarDays, BarChart3, ChevronDown, ChevronUp, Download, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

function formatTime(isoStr) {
  if (!isoStr) return '--:--';
  return new Date(isoStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T12:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
}

function hoursLabel(h) {
  if (!h && h !== 0) return '--';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h ${mm.toString().padStart(2, '0')}m`;
}

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function ManagerClockDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Today tab
  const [todayData, setTodayData] = useState([]);
  const [loadingToday, setLoadingToday] = useState(true);

  // History tab
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [teamUsers, setTeamUsers] = useState([]);
  const [expandedUsers, setExpandedUsers] = useState({});

  // Summary tab
  const [summaryData, setSummaryData] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
  const [summaryMonth, setSummaryMonth] = useState(new Date().getMonth() + 1);

  const loadToday = useCallback(async () => {
    setLoadingToday(true);
    try {
      const res = await clockApi.teamToday();
      setTodayData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingToday(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (selectedUser !== 'all') params.user_id = selectedUser;
      const [histRes, usersRes] = await Promise.all([
        clockApi.teamHistory(params),
        userApi.list({ team_only: true }),   // always show only direct reports
      ]);
      setHistoryData(histRes.data);
      setTeamUsers(usersRes.data);
    } catch (e) { console.error(e); }
    finally { setLoadingHistory(false); }
  }, [dateFrom, dateTo, selectedUser]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await clockApi.teamSummary({ year: summaryYear, month: summaryMonth });
      setSummaryData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingSummary(false); }
  }, [summaryYear, summaryMonth]);

  useEffect(() => { loadToday(); }, [loadToday]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const filteredHistory = useMemo(() =>
    historyData.filter(u => !search || u.user_name?.toLowerCase().includes(search.toLowerCase())),
    [historyData, search]
  );
  const filteredToday = useMemo(() =>
    todayData.filter(u => !search || u.user_name?.toLowerCase().includes(search.toLowerCase())),
    [todayData, search]
  );

  const toggleUser = (uid) => setExpandedUsers(prev => ({ ...prev, [uid]: !prev[uid] }));

  const exportCSV = () => {
    const rows = [['Empleado', 'Fecha', 'Entrada', 'Salida', 'Horas']];
    historyData.forEach(u => {
      u.days?.forEach(d => {
        const entry = d.records.find(r => r.type === 'entry');
        const exit = d.records.find(r => r.type === 'exit');
        rows.push([u.user_name, d.date, entry ? formatTime(entry.timestamp) : '', exit ? formatTime(exit.timestamp) : '', d.total_hours || '']);
      });
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `fichajes-${dateFrom}-${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Today stats
  const inCount = todayData.filter(u => u.status === 'in').length;
  const outCount = todayData.filter(u => u.status === 'out').length;
  const notClockedCount = todayData.filter(u => u.status === 'not_clocked').length;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Clock className="h-6 w-6 text-brand" />
            Control de Fichajes
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Seguimiento de fichajes de teletrabajo del equipo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar empleado..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs w-40"
              data-testid="clock-search"
            />
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Actualmente en teletrabajo', value: inCount, color: 'text-blue-600 dark:text-blue-400', dot: 'bg-blue-500 animate-pulse' },
          { label: 'Han terminado hoy', value: outCount, color: 'text-green-600 dark:text-green-400', dot: 'bg-green-500' },
          { label: 'Sin fichar hoy', value: notClockedCount, color: 'text-muted-foreground', dot: 'bg-gray-400' },
        ].map(s => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className={`h-2 w-2 rounded-full ${s.dot}`} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <div className={`text-3xl font-bold font-heading ${s.color}`}>{s.value}</div>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="today" className="space-y-4">
        <TabsList>
          <TabsTrigger value="today" data-testid="tab-today">
            <Clock className="h-3.5 w-3.5 mr-1.5" />Hoy
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <CalendarDays className="h-3.5 w-3.5 mr-1.5" />Histórico
          </TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" />Resumen mensual
          </TabsTrigger>
        </TabsList>

        {/* TODAY TAB */}
        <TabsContent value="today">
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={loadToday} className="gap-1.5 h-7 text-xs" data-testid="refresh-today">
              <RefreshCw className="h-3.5 w-3.5" />Actualizar
            </Button>
          </div>
          {loadingToday ? (
            <div className="flex justify-center py-10"><Loader className="h-6 w-6 animate-spin text-brand" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredToday.length === 0
                ? <div className="col-span-2 text-center py-8 text-muted-foreground text-sm">No hay datos</div>
                : filteredToday.map(emp => (
                <motion.div
                  key={emp.user_id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className={`border rounded-xl p-4 shadow-sm ${
                    emp.status === 'in' ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800'
                    : emp.status === 'out' ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800'
                    : 'bg-card border-border'
                  }`}
                  data-testid={`today-card-${emp.user_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-sm font-heading">{emp.user_name}</div>
                      <div className={`inline-flex items-center gap-1.5 text-xs mt-1 px-2 py-0.5 rounded-full font-medium ${
                        emp.status === 'in' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : emp.status === 'out' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-muted text-muted-foreground'
                      }`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${emp.status === 'in' ? 'bg-blue-500 animate-pulse' : emp.status === 'out' ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {emp.status === 'in' ? 'En teletrabajo' : emp.status === 'out' ? 'Finalizado' : 'Sin fichar'}
                      </div>
                    </div>
                    {emp.hours_so_far != null && (
                      <div className="text-right">
                        <div className="text-sm font-bold">{hoursLabel(emp.hours_so_far)}</div>
                        <div className="text-xs text-muted-foreground">{emp.status === 'in' ? 'acumuladas' : 'trabajadas'}</div>
                      </div>
                    )}
                  </div>
                  {(emp.entry_time || emp.exit_time) && (
                    <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                      {emp.entry_time && (
                        <span className="flex items-center gap-1">
                          <LogIn className="h-3 w-3 text-blue-500" />{formatTime(emp.entry_time)}
                        </span>
                      )}
                      {emp.exit_time && (
                        <span className="flex items-center gap-1">
                          <LogOut className="h-3 w-3 text-orange-500" />{formatTime(emp.exit_time)}
                        </span>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground h-8" />
            <span className="text-xs text-muted-foreground">—</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground h-8" />
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid="select-user">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {teamUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={loadHistory} className="h-8 gap-1.5 text-xs">
              <RefreshCw className="h-3.5 w-3.5" />Filtrar
            </Button>
            <Button size="sm" variant="outline" onClick={exportCSV} className="h-8 gap-1.5 text-xs ml-auto">
              <Download className="h-3.5 w-3.5" />Exportar CSV
            </Button>
          </div>

          {loadingHistory ? (
            <div className="flex justify-center py-10"><Loader className="h-6 w-6 animate-spin text-brand" /></div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Sin registros para el período seleccionado</div>
          ) : (
            <div className="space-y-3" data-testid="history-table">
              {filteredHistory.map(emp => (
                <div key={emp.user_id} className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                  <button
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors"
                    onClick={() => toggleUser(emp.user_id)}
                    data-testid={`toggle-employee-${emp.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-brand/10 flex items-center justify-center text-xs font-bold text-brand">
                        {emp.user_name?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-sm font-heading">{emp.user_name}</div>
                        <div className="text-xs text-muted-foreground">{emp.days?.length || 0} días trabajados</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-bold text-brand text-sm">{hoursLabel(emp.total_hours)}</div>
                        <div className="text-xs text-muted-foreground">total</div>
                      </div>
                      {expandedUsers[emp.user_id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </button>

                  {expandedUsers[emp.user_id] && (
                    <div className="border-t border-border">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/30">
                            <th className="text-left px-5 py-2 text-muted-foreground font-medium">Fecha</th>
                            <th className="text-left px-4 py-2 text-muted-foreground font-medium">Entrada</th>
                            <th className="text-left px-4 py-2 text-muted-foreground font-medium">Salida</th>
                            <th className="text-right px-5 py-2 text-muted-foreground font-medium">Horas</th>
                            {isAdmin && <th className="text-left px-4 py-2 text-muted-foreground font-medium">Ubicación</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {emp.days?.map(day => {
                            const entry = day.records.find(r => r.type === 'entry');
                            const exit = day.records.find(r => r.type === 'exit');
                            return (
                              <tr key={day.date} className="border-t border-border/50 hover:bg-muted/20">
                                <td className="px-5 py-2 capitalize">{formatDate(day.date)}</td>
                                <td className="px-4 py-2 text-blue-600 dark:text-blue-400">
                                  {entry ? formatTime(entry.timestamp) : <span className="text-muted-foreground">—</span>}
                                </td>
                                <td className="px-4 py-2 text-orange-600 dark:text-orange-400">
                                  {exit ? formatTime(exit.timestamp) : <span className="text-muted-foreground">—</span>}
                                </td>
                                <td className="px-5 py-2 text-right font-semibold">{hoursLabel(day.total_hours)}</td>
                                {isAdmin && (
                                  <td className="px-4 py-2 text-muted-foreground">
                                    {entry?.geo_city ? (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {[entry.geo_city, entry.geo_country].filter(Boolean).join(', ')}
                                      </span>
                                    ) : '—'}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SUMMARY TAB */}
        <TabsContent value="summary">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Select value={String(summaryMonth)} onValueChange={v => setSummaryMonth(Number(v))}>
              <SelectTrigger className="h-8 text-xs w-32" data-testid="select-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(summaryYear)} onValueChange={v => setSummaryYear(Number(v))}>
              <SelectTrigger className="h-8 text-xs w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={loadSummary} className="h-8 gap-1.5 text-xs">
              <RefreshCw className="h-3.5 w-3.5" />Cargar
            </Button>
          </div>

          {loadingSummary ? (
            <div className="flex justify-center py-10"><Loader className="h-6 w-6 animate-spin text-brand" /></div>
          ) : summaryData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Sin registros en ese período</div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm" data-testid="summary-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b border-border">
                    <th className="text-left px-5 py-3 font-medium text-muted-foreground">Empleado</th>
                    <th className="text-center px-4 py-3 font-medium text-muted-foreground">Días trabajados</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Media/día</th>
                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">Total horas</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((emp, i) => (
                    <tr key={emp.user_id} className={`border-t border-border/50 hover:bg-muted/20 ${i === 0 ? 'bg-brand/5' : ''}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {i === 0 && <span className="text-xs text-brand font-bold">↑</span>}
                          <span className="font-medium">{emp.user_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-muted px-2 py-0.5 rounded-full text-xs font-medium">{emp.days_worked}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground text-xs">{hoursLabel(emp.avg_hours_per_day)}</td>
                      <td className="px-5 py-3 text-right font-bold text-brand">{hoursLabel(emp.total_hours)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-border bg-muted/20">
                  <tr>
                    <td className="px-5 py-3 font-semibold text-sm" colSpan={2}>Total equipo</td>
                    <td />
                    <td className="px-5 py-3 text-right font-bold text-brand">
                      {hoursLabel(summaryData.reduce((s, e) => s + e.total_hours, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
